import React, { memo, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import CodeMirror from '@uiw/react-codemirror'
import { Card } from 'transfix-ui/components/Card'
import SidePanel from 'transfix-ui/components/SidePanel'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box, createStyles, makeStyles, Typography } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid'
import { theme } from 'transfix-ui/theme'
import { brandColors } from 'transfix-ui/theme/colors'
import { shadows } from 'transfix-ui/theme/shadows'
import { IInboundFileRouterConfig } from 'types/graphqlTypes'
import { capitalize } from 'utils/capitalize'
import { ActiveStatus } from '../ActiveStatus'
import { ConnectionConfigDetailsGrid } from '../ConnectionConfigs/ConnectionConfigGrids/ConnectionConfigDetailsGrid'
import { useEdiInboundFileRouterConfigLazyQuery } from './graphql/EdiInboundFileRouterConfig'

export interface IInboundFileRouterConfigDetailsProps {
  onSidebarClose: () => void
}

const useInboundFileRouterConfigDetailsStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
      boxShadow: shadows[3],
    },
    editor: {
      borderRight: '1px solid',
      '& .cm-content': {
        border: '1px solid',
      },
      '& .cm-lineNumbers': {
        minWidth: 33,
      },
      '& .cm-line::selection': {
        backgroundColor: '#d9d9d9 !important',
      },
    },
    titleText: {
      display: 'flex',
      flex: theme.spacing(1),
      alignItems: 'center',
    },
    secondary: {
      color: brandColors.coolGray5,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  })
)

const getCells = (InboundFileRouterConfigDetails: IInboundFileRouterConfig) => {
  const { connectionType, createdAt, environment, host, pathPrefix, port, secretKey, username } =
    InboundFileRouterConfigDetails || {}

  return [
    { label: 'Created At', value: createdAt },
    { label: 'Connection Type', value: connectionType },
    { label: 'Environment', value: capitalize(environment) },
    { label: 'Host', value: host },
    { label: 'Port', value: port },
    { label: 'Username', value: username },
    { label: 'Path Prefix', value: pathPrefix },
    { label: 'Secret Key', value: secretKey },
  ]
}

export const InboundFileRouterConfigDetails = memo<IInboundFileRouterConfigDetailsProps>(
  ({ onSidebarClose }) => {
    const { id: inboundFileRouterConfigId } = useParams<{ id: string }>()

    const classes = useInboundFileRouterConfigDetailsStyles()

    const [
      getEdiInboundFileRouterConfig,
      { data, loading },
    ] = useEdiInboundFileRouterConfigLazyQuery({
      variables: {
        id: String(inboundFileRouterConfigId),
      },
      notifyOnNetworkStatusChange: true,
    })

    /* Loading the configs when page loads */
    useEffect(() => {
      getEdiInboundFileRouterConfig()
    }, [getEdiInboundFileRouterConfig])

    const { ediInboundFileRouterConfig } = data || {}

    const { disabled, shipperCodeJson, tmsName } = ediInboundFileRouterConfig || {}

    const cells = useMemo(() => getCells(ediInboundFileRouterConfig as IInboundFileRouterConfig), [
      ediInboundFileRouterConfig,
    ])

    return (
      <SidePanel anchor='right' onClose={onSidebarClose} PaperProps={{ style: { width: '86%' } }}>
        {loading && <Spinner />}
        <Box mb={3} data-test='SidePanel'>
          <Box display='flex' justifyContent='center' alignItems='center' pl={2} pr={2}>
            <Typography variant='h2' className={classes.titleText}>
              <span data-test='inbound-file-router-connection-config-id-header'>
                {inboundFileRouterConfigId}
              </span>
              <span className={classes.secondary}>|</span>
              <span
                className='mr3'
                data-test='inbound-file-router-connection-config-identifier-header'>
                {`${capitalize(tmsName?.toLowerCase())}`}
              </span>
              <span data-test='inbound-file-router-connection-config-active-status'>
                <ActiveStatus disabled={disabled} />
              </span>
            </Typography>
          </Box>
        </Box>
        <hr />
        <Grid
          container
          justifyContent='flex-start'
          spacing={1}
          data-test='inbound-file-router-config-details-grid'>
          <Grid item lg={8} sm={12} xs={12} md={12}>
            <Card className={classes.card}>
              {cells.map(cell => (
                <ConnectionConfigDetailsGrid
                  key={`cell-${inboundFileRouterConfigId}-${cell.label}`}
                  gridHash={cell}
                />
              ))}
            </Card>
          </Grid>
        </Grid>
        <Typography variant='h3'>Shipper Code JSON</Typography>
        <Box mt={2}>
          <CodeMirror
            data-test='inbound-file-router-config-shipper-code-json'
            height='auto'
            width='30vw'
            className={classes.editor}
            value={loading ? 'Loading JSON Config...' : JSON.stringify(shipperCodeJson, null, 2)}
            editable={false}
          />
        </Box>
      </SidePanel>
    )
  }
)
